import { Box } from '@chakra-ui/react';
import {
  useEffect,
  useRef,
  type ComponentType,
  type FC,
  type ReactNode,
  type RefObject,
} from 'react';

import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { LoadingMaterialCard } from '$/components/core/Collection/LoadingMaterialCard';
import { MaterialCard } from '$/components/core/Collection/MaterialCard';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { useMaterials } from '$/hooks/data/useMaterials';
import { useMaterialGridMaxItems } from '$/hooks/useMaterialGridMaxItems';
import { useWindowSize } from '$/hooks/useWindowSize';
import {
  type Material,
  type MaterialType,
} from '$/services/usecases/materials/mapper/materials';
import { repeat } from '$/utils/arrayUtils';

interface Props {
  headline: string;
  type: MaterialType;
  RowWrapper: ComponentType<{ children: ReactNode }>;
  hideFavoriteButtons?: boolean;
  minCardAmount?: number;
  cardLimit?: number;
  showAllUrl?: string;
  wrapperRef?: RefObject<HTMLDivElement | null>;
  onResult?: (count: number, type: MaterialType) => void;
  onShowMoreClick?: () => void;
  onSelect: (material: Material) => void;
}

export const MaterialRow: FC<Props> = ({
  headline,
  showAllUrl,
  type,
  wrapperRef,
  RowWrapper,
  onShowMoreClick,
  hideFavoriteButtons = false,
  minCardAmount = 1,
  cardLimit = 100,
  onResult = () => {},
  onSelect,
}) => {
  const materialWrapperRef = useRef<HTMLDivElement>(null);

  const { hasPermission } = useAuthorization();
  const openAuthenticationModal = useAuthenticationStore.useOnModalOpen();

  const hasCollectionPermission = hasPermission('View_All_Collections');
  const isDemoSection = type !== 'color_tint' && !hasCollectionPermission;

  const [{ isLoading }, showSkeleton, materials, count] = useMaterials({
    limit: 10,
    type,
    isDemo: isDemoSection,
  });

  useEffect(() => {
    if (materials) onResult(count, type);
  }, [materials, count, onResult, type]);

  useWindowSize();

  const materialCardLimit = Math.max(
    minCardAmount,
    Math.min(cardLimit, useMaterialGridMaxItems()),
  );

  if ((isLoading && !showSkeleton) || (!isLoading && materials?.length === 0)) {
    return null;
  }

  const wrapperHeaderHeight = 150;
  const sectionHeaderHeight = (36 + 24) * 3;
  const gridGap = 24;

  const wrapperHeight = wrapperRef?.current?.offsetHeight;
  const cardHeight = materialWrapperRef.current?.offsetHeight;

  const possibleCards =
    wrapperHeight && cardHeight
      ? Math.floor(
          (wrapperHeight - sectionHeaderHeight - wrapperHeaderHeight) /
            (cardHeight + gridGap),
        )
      : undefined;

  const rowsPerSection = possibleCards
    ? Math.max(1, Math.ceil(possibleCards / 3))
    : undefined;

  const visibleCards =
    rowsPerSection && materialCardLimit
      ? materialCardLimit * rowsPerSection
      : undefined;

  const visibleMaterials =
    materials?.slice(0, visibleCards ?? materialCardLimit) ?? [];

  return (
    <SectionHeadline
      headline={headline}
      showAllUrl={!isDemoSection ? showAllUrl : undefined}
      onShowMoreClick={onShowMoreClick}
    >
      <RowWrapper>
        {isLoading &&
          repeat(materialCardLimit ?? 0).map((key) => (
            <LoadingMaterialCard key={key} />
          ))}

        {visibleMaterials.map((material, index) => (
          <Box key={index} ref={index === 0 ? materialWrapperRef : undefined}>
            <MaterialCard
              editMode={hideFavoriteButtons ? 'none' : undefined}
              material={material}
              onClick={() => {
                if (!isDemoSection) onSelect(material);
                else openAuthenticationModal('versionComparison');
              }}
              materialType={type}
            />
          </Box>
        ))}
      </RowWrapper>
    </SectionHeadline>
  );
};
